import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop'
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { Subject, takeUntil, finalize } from 'rxjs'
import { PersonaService } from '../../../../services/persona/persona.service'
import {
  PersonaData,
  PersonaScenarioData,
} from '../../../../services/persona/persona.mapping'
import {
  CreatePersonaInput,
  UpdatePersonaInput,
} from '../../../../../_generated/graphql'
import {
  SelectComponent,
  Option,
} from '../../../../components/select/select.component'
import {
  MultipleSelectComponent,
  Item,
} from '../../../../components/multiple-select/multiple-select.component'
import { SkillMap } from '../../../../services/assessment/assessment.mapping'
import { AssessmentStore } from '../../../../stores/assessment.store'
import { MessageDialogService } from '../../../../components/message-dialog/message-dialog.service'

@Component({
  selector: 'app-persona-list-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatIconModule,
    SelectComponent,
    MultipleSelectComponent,
  ],
  templateUrl: './persona-list-page.component.html',
  styleUrl: './persona-list-page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AssessmentStore],
})
export class PersonaListPageComponent implements OnInit, OnDestroy {
  personas: PersonaData[] = []
  isLoading = false
  isDeleting = false
  isSubmitting = false
  isSavingOrder = false
  private serverOrderIds: string[] = []
  showForm = false
  editingPersona: PersonaData | null = null
  deleteConfirm = { show: false, persona: null as PersonaData | null }
  personaForm!: FormGroup

  skillMaps: SkillMap[] = []
  skillMapOptions: Option[] = []
  selectedSkillMap: Option = { id: '', name: '' }
  phaseItems: Item[] = []

  private destroy$ = new Subject<void>()

  constructor(
    private personaService: PersonaService,
    private fb: FormBuilder,
    private assessmentStore: AssessmentStore,
    private messageDialogService: MessageDialogService,
  ) {}

  ngOnInit(): void {
    this.initForm()
    this.loadPersonas()
    this.loadSkillMaps()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initForm(): void {
    this.personaForm = this.fb.group({
      name: ['', [Validators.required]],
      gender: ['', [Validators.required]],
      occupation: ['', [Validators.required]],
      literacyLevel: ['', [Validators.required]],
      scene: [''],
      purpose: [''],
      estimatedMinutes: ['', [Validators.pattern(/^-?[0-9]+$/)]],
      scenarios: this.fb.array([], [Validators.required]),
    })
  }

  get scenarios(): FormArray {
    return this.personaForm.get('scenarios') as FormArray
  }

  createScenarioFormGroup(scenario?: PersonaScenarioData): FormGroup {
    return this.fb.group({
      id: [scenario?.id || ''],
      name: [scenario?.name || '', [Validators.required]],
      scenarioSetting: [scenario?.scenarioSetting || '', [Validators.required]],
      weight: [scenario?.weight || 1, [Validators.required, Validators.min(1)]],
    })
  }

  addScenario(): void {
    this.scenarios.push(this.createScenarioFormGroup())
  }

  removeScenario(index: number): void {
    this.scenarios.removeAt(index)
  }

  private resetForm(): void {
    this.personaForm.reset()
    this.scenarios.clear()
    this.isSubmitting = false
    this.selectedSkillMap = { id: '', name: '' }
    this.phaseItems = []
  }

  private populateForm(persona: PersonaData): void {
    this.personaForm.patchValue({
      name: persona.name,
      gender: persona.gender,
      occupation: persona.occupation,
      literacyLevel: persona.literacyLevel,
      scene: persona?.scene || '',
      purpose: persona?.purpose || '',
      estimatedMinutes: persona?.estimatedMinutes ?? '',
    })

    this.scenarios.clear()
    if (persona.scenarios && persona.scenarios.length > 0) {
      persona.scenarios.forEach((scenario) => {
        this.scenarios.push(this.createScenarioFormGroup(scenario))
      })
    }

    if (persona.skillMapId) {
      const foundOption = this.skillMapOptions.find(
        (op) => op.id === persona.skillMapId,
      )
      if (foundOption) {
        this.selectedSkillMap = foundOption

        const skillMap = this.skillMaps.find(
          (sm) => sm.id === persona.skillMapId,
        )

        if (skillMap) {
          this.phaseItems = skillMap.phases.map((phase) => ({
            id: phase.id,
            label: phase.name,
            checked: persona.phaseIds.includes(phase.id),
          }))
        }
      } else {
        this.selectedSkillMap = { id: '', name: '' }
        this.phaseItems = []
      }
    } else {
      this.selectedSkillMap = { id: '', name: '' }
      this.phaseItems = []
    }
  }

  loadPersonas(): void {
    this.isLoading = true
    this.personaService
      .getPersonas()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false
        }),
      )
      .subscribe({
        next: (personas) => {
          this.personas = personas
          this.serverOrderIds = personas.map((p) => p.id)
        },
        error: (error) => {
          console.error('Error loading personas:', error)
          this.personas = []
          this.serverOrderIds = []
          this.messageDialogService.showError('ペルソナの取得に失敗しました。')
        },
      })
  }

  get isOrderChanged(): boolean {
    return (
      this.personas.length !== this.serverOrderIds.length ||
      this.personas.some((p, i) => p.id !== this.serverOrderIds[i])
    )
  }

  onDrop(event: CdkDragDrop<PersonaData[]>): void {
    moveItemInArray(this.personas, event.previousIndex, event.currentIndex)
  }

  saveOrder(): void {
    if (this.isSavingOrder) return
    this.isSavingOrder = true
    this.personaService
      .reorderPersonas(this.personas.map((p) => p.id))
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSavingOrder = false)),
      )
      .subscribe({
        next: () => {
          this.loadPersonas()
        },
        error: (error) => {
          console.error('Error saving persona order:', error)
          this.messageDialogService.showError(
            '並び順の保存に失敗しました。一覧が更新された可能性があります。キャンセルしてからやり直してください。',
          )
        },
      })
  }

  cancelReorder(): void {
    this.loadPersonas()
  }

  showCreateForm(): void {
    this.editingPersona = null
    this.resetForm()
    this.showForm = true
    this.scrollToTop()
  }

  editPersona(persona: PersonaData): void {
    this.editingPersona = persona
    // スキルマップがロード済みならすぐに表示、未ロードなら待つ
    if (this.skillMaps.length > 0) {
      this.populateForm(persona)
    } else {
      // スキルマップのロードを待ってからフォームに反映
      this.assessmentStore
        .getSkillMaps()
        .pipe(takeUntil(this.destroy$))
        .subscribe((skillMaps) => {
          this.skillMaps = skillMaps
          this.skillMapOptions = skillMaps.map((skillMap: SkillMap) => ({
            id: skillMap.id,
            name: skillMap.name,
          }))
          this.populateForm(persona)
        })
    }
    this.showForm = true
    this.scrollToTop()
  }

  duplicatePersona(persona: PersonaData): void {
    this.editingPersona = null
    const duplicatedPersona = {
      ...persona,
      name: `${persona.name}のコピー`,
      scenarios: persona.scenarios.map((scenario) => ({
        ...scenario,
        id: '',
      })),
    }
    // スキルマップがロード済みならすぐに表示、未ロードなら待つ
    if (this.skillMaps.length > 0) {
      this.populateForm(duplicatedPersona)
    } else {
      // スキルマップのロードを待ってからフォームに反映
      this.assessmentStore
        .getSkillMaps()
        .pipe(takeUntil(this.destroy$))
        .subscribe((skillMaps) => {
          this.skillMaps = skillMaps
          this.skillMapOptions = skillMaps.map((skillMap: SkillMap) => ({
            id: skillMap.id,
            name: skillMap.name,
          }))
          this.populateForm(duplicatedPersona)
        })
    }
    this.showForm = true
    this.scrollToTop()
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  hideForm(): void {
    this.showForm = false
    this.editingPersona = null
    this.resetForm()
  }

  onSubmit(): void {
    if (this.personaForm.valid && !this.isSubmitting) {
      // バリデーション：スキルマップが選択されているか
      if (!this.selectedSkillMap.id || this.selectedSkillMap.id === '') {
        alert('スキルマップを選択してください')
        return
      }

      // バリデーション：該当フェーズが1つ以上選択されているか
      const selectedPhaseIds = this.phaseItems
        .filter((item) => item.checked)
        .map((item) => item.id)
      if (selectedPhaseIds.length === 0) {
        alert('該当フェーズを1つ以上選択してください')
        return
      }

      this.isSubmitting = true
      const formValue = this.personaForm.value

      const scenarios = formValue.scenarios.map(
        (scenario: {
          id: string
          name: string
          weight: number
          scenarioSetting: string
        }) => ({
          id: scenario.id || undefined,
          name: scenario.name,
          scenarioSetting: scenario.scenarioSetting,
          weight: scenario.weight || 1,
        }),
      )

      const processedFormValue = {
        ...formValue,
        scene: formValue?.scene?.trim() ? formValue.scene.trim() : null,
        purpose: formValue?.purpose?.trim() ? formValue.purpose.trim() : null,
        estimatedMinutes:
          formValue.estimatedMinutes === '' ||
          formValue.estimatedMinutes == null
            ? null
            : Number(formValue.estimatedMinutes),
        skillMapId: this.selectedSkillMap.id,
        phaseIds: selectedPhaseIds,
        scenarios,
      }

      if (this.editingPersona) {
        const updateData = {
          id: this.editingPersona.id,
          ...processedFormValue,
        } as UpdatePersonaInput
        this.personaService
          .updatePersona(updateData)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isSubmitting = false)),
          )
          .subscribe({
            next: () => {
              this.hideForm()
              this.loadPersonas()
            },
            error: (error) => {
              console.error('Error updating persona:', error)
            },
          })
      } else {
        // Create
        const createData = processedFormValue as CreatePersonaInput
        this.personaService
          .createPersona(createData)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isSubmitting = false)),
          )
          .subscribe({
            next: () => {
              this.hideForm()
              this.loadPersonas()
            },
            error: (error) => {
              console.error('Error creating persona:', error)
            },
          })
      }
    }
  }

  showDeleteConfirm(persona: PersonaData): void {
    this.deleteConfirm = { show: true, persona }
  }

  hideDeleteConfirm(): void {
    this.deleteConfirm = { show: false, persona: null }
  }

  handleDeletePersona(): void {
    if (!this.deleteConfirm.persona) return

    this.isDeleting = true
    this.personaService
      .deletePersona(this.deleteConfirm.persona.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isDeleting = false)),
      )
      .subscribe({
        next: () => {
          this.hideDeleteConfirm()
          this.loadPersonas()
        },
        error: (error) => {
          console.error('Error deleting persona:', error)
        },
      })
  }

  loadSkillMaps(): void {
    this.assessmentStore
      .getSkillMaps()
      .pipe(takeUntil(this.destroy$))
      .subscribe((skillMaps) => {
        this.skillMaps = skillMaps
        this.skillMapOptions = skillMaps.map((skillMap: SkillMap) => ({
          id: skillMap.id,
          name: skillMap.name,
        }))
      })
  }

  changeSkillMap(option: Option): void {
    this.selectedSkillMap = this.skillMapOptions.find(
      (op) => op.id === option.id,
    ) ?? { id: '', name: '' }
    this.phaseItems =
      this.skillMaps
        .find((skillMap) => skillMap.id === option.id)
        ?.phases.map((phase) => ({
          id: phase.id,
          label: phase.name,
          checked: false,
        })) ?? []
  }

  changeSelectedPhases(items: Item[]): void {
    this.phaseItems = items
  }
}
